/**
 * Client-Side Admin Permission Checker
 *
 * Checks if a user has admin permissions by querying the admins collection in Firestore.
 * This provides client-side UI protection (e.g., showing/hiding admin menu items).
 *
 * IMPORTANT: This is client-side only and can be bypassed. All actual security
 * enforcement happens server-side in Cloud Functions using requireSuperAdmin().
 *
 * The client-side check is for UX purposes only - to show the right UI to the right users.
 */

import React from "react";
import { doc, getDoc } from "firebase/firestore";

/**
 * Check if a user has admin permissions
 *
 * @param {string} userId - The Firebase Auth UID to check
 * @param {FirebaseFirestore} db - Firestore database instance
 * @returns {Promise<{isAdmin: boolean, isSuperAdmin: boolean, isT3Admin: boolean, error: string|null}>}
 *
 * @example
 * const permissions = await checkAdminPermissions(user.uid, db);
 * if (permissions.isSuperAdmin) {
 *   // Show superAdmin menu
 * }
 */
export const checkAdminPermissions = async (userId, db) => {
  if (!userId || !db) {
    return {
      isAdmin: false,
      isSuperAdmin: false,
      isT3Admin: false,
      error: "Missing userId or db instance",
    };
  }

  try {
    const adminsDocRef = doc(db, "admins", "permissions");
    const adminsDoc = await getDoc(adminsDocRef);

    if (!adminsDoc.exists()) {
      console.warn("⚠️ Admins collection not found - no users have admin access");
      return {
        isAdmin: false,
        isSuperAdmin: false,
        isT3Admin: false,
        error: "Admins collection not configured",
      };
    }

    const data = adminsDoc.data();
    const isSuperAdmin = data.superAdmins?.includes(userId) || false;
    const isT3Admin = data.T3Admins?.includes(userId) || false;
    const isAdmin = isSuperAdmin || isT3Admin;
    const inConversionAccess = data.conversionAccess?.includes(userId) || false;
    // Super Admins always have Conversion access; T3 Admins must be explicitly listed.
    const hasConversionAccess = isSuperAdmin || (isT3Admin && inConversionAccess);

    return {
      isAdmin,
      isSuperAdmin,
      isT3Admin,
      hasConversionAccess,
      error: null,
    };
  } catch (error) {
    console.error("❌ Error checking admin permissions:", error);
    return {
      isAdmin: false,
      isSuperAdmin: false,
      isT3Admin: false,
      hasConversionAccess: false,
      error: error.message || "Failed to check admin permissions",
    };
  }
};

/**
 * React Hook for checking admin permissions
 * Use this in components that need to show/hide admin UI elements
 *
 * @param {string} userId - The Firebase Auth UID
 * @param {FirebaseFirestore} db - Firestore database instance
 * @returns {{
 *   isAdmin: boolean,
 *   isSuperAdmin: boolean,
 *   isT3Admin: boolean,
 *   loading: boolean,
 *   error: string|null
 * }}
 *
 * @example
 * function AdminButton() {
 *   const { user } = useContext(UserContext);
 *   const authHook = useAuth();
 *   const { isSuperAdmin, loading } = useAdminPermissions(user?.uid, authHook.db);
 *
 *   if (loading) return <CircularProgress />;
 *   if (!isSuperAdmin) return null;
 *   return <Button>Admin Action</Button>;
 * }
 */
export const useAdminPermissions = (userId, db) => {
  const [permissions, setPermissions] = React.useState({
    isAdmin: false,
    isSuperAdmin: false,
    isT3Admin: false,
    hasConversionAccess: false,
    loading: true,
    error: null,
  });

  React.useEffect(() => {
    if (!userId || !db) {
      setPermissions({
        isAdmin: false,
        isSuperAdmin: false,
        isT3Admin: false,
        hasConversionAccess: false,
        loading: false,
        error: "Missing userId or db instance",
      });
      return;
    }

    let mounted = true;

    const checkPermissions = async () => {
      const result = await checkAdminPermissions(userId, db);
      if (mounted) {
        setPermissions({
          ...result,
          loading: false,
        });
      }
    };

    checkPermissions();

    return () => {
      mounted = false;
    };
  }, [userId, db]);

  return permissions;
};

/**
 * React Hook for checking if user is a system admin (superAdmin OR T3Admin)
 * Simpler version of useAdminPermissions for components that just need
 * to know if the user has any system-level admin access
 *
 * @param {string} userId - The Firebase Auth UID
 * @param {FirebaseFirestore} db - Firestore database instance
 * @returns {{
 *   isSystemAdmin: boolean,
 *   loading: boolean,
 *   error: string|null
 * }}
 *
 * @example
 * function AdminPage() {
 *   const { user } = useContext(UserContext);
 *   const authHook = useAuth();
 *   const { isSystemAdmin, loading } = useIsSystemAdmin(user?.uid, authHook.db);
 *
 *   if (loading) return <CircularProgress />;
 *   if (!isSystemAdmin) return <Alert>Access Denied</Alert>;
 *   return <AdminContent />;
 * }
 */
export const useIsSystemAdmin = (userId, db) => {
  const { isAdmin, loading, error } = useAdminPermissions(userId, db);

  return {
    isSystemAdmin: isAdmin, // isAdmin = isSuperAdmin OR isT3Admin
    loading,
    error,
  };
};
